import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Heart, DollarSign, Home, Car, ShoppingBag, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DonatePage() {
  const [amount, setAmount] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const presetAmounts = [
    { value: "25", label: "$25", impact: "One night of lodging" },
    { value: "50", label: "$50", impact: "Two nights + meals" },
    { value: "100", label: "$100", impact: "Week of transportation" },
    { value: "250", label: "$250", impact: "Full family support package" }
  ];

  const impactItems = [
    { icon: Home, label: "Lodging", desc: "$25 covers one night" },
    { icon: Car, label: "Transportation", desc: "$15 per day" },
    { icon: ShoppingBag, label: "Necessities", desc: "$10 per day" }
  ];

  const handlePresetClick = (value) => {
    setAmount(value);
    setCustomAmount("");
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    setAmount("");
  };

  const getFinalAmount = () => {
    if (customAmount) return parseFloat(customAmount);
    if (amount) return parseFloat(amount);
    return 0;
  };

  const handleDonate = async () => {
    const finalAmount = getFinalAmount();
    
    if (finalAmount < 1) {
      toast.error("Please enter a donation amount of at least $1");
      return;
    }

    setIsProcessing(true);
    
    try {
      const response = await axios.post(`${API}/donations/checkout`, {
        amount: finalAmount,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        message: message || null,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Donation error:", error);
      toast.error("Failed to process donation. Please try again.");
      setIsProcessing(false);
    }
  };

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1758599669406-d5179ccefcb9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwZ3JvdXAlMjBvZiUyMGhhcHB5JTIwdm9sdW50ZWVyc3xlbnwwfHx8fDE3NzAzNDc1NzN8MA&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/70 to-foreground/50" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl space-y-6 animate-fade-in-up">
            <span className="text-accent font-medium text-sm uppercase tracking-wider">Support Our Mission</span>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight">
              Give the Gift of <span className="text-accent">Hope</span>
            </h1>
            <p className="text-lg text-white/80 leading-relaxed">
              Your donation helps families stay close to their loved ones during 
              hospital stays. Every dollar goes directly to lodging, transportation, 
              and daily necessities for families in need.
            </p>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {impactItems.map((item, index) => (
              <div key={index} className="flex items-center gap-4 text-primary-foreground" data-testid={`impact-item-${index}`}>
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <item.icon className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-semibold">{item.label}</div>
                  <div className="text-primary-foreground/70 text-sm">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <Card className="bg-card border-border/50 shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-accent" />
              </div>
              <CardTitle className="font-serif text-2xl">Make a Donation</CardTitle>
              <CardDescription>
                Choose a donation amount or enter a custom amount below.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-8 space-y-8">
              {/* Preset Amounts */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {presetAmounts.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => handlePresetClick(preset.value)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      amount === preset.value
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid={`preset-amount-${preset.value}`}
                  >
                    <div className="font-serif text-2xl font-bold text-foreground">
                      {preset.label}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {preset.impact}
                    </div>
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="space-y-2">
                <Label htmlFor="customAmount">Or enter a custom amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="customAmount"
                    type="number"
                    min="1"
                    step="0.01"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    placeholder="Enter amount"
                    className="pl-12 h-12 input-focus"
                    data-testid="custom-amount-input"
                  />
                </div>
              </div>

              {/* Donor Info */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="donorName">Your Name (Optional)</Label>
                    <Input
                      id="donorName"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      placeholder="John Doe"
                      className="input-focus"
                      data-testid="donor-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="donorEmail">Your Email (Optional)</Label>
                    <Input
                      id="donorEmail"
                      type="email"
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      placeholder="john@example.com"
                      className="input-focus"
                      data-testid="donor-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Leave a Message (Optional)</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Share why you're supporting Hope Hospitality..."
                    rows={3}
                    className="input-focus resize-none"
                    data-testid="donation-message-input"
                  />
                </div>
              </div>

              {/* Total */}
              {getFinalAmount() > 0 && (
                <div className="bg-secondary rounded-xl p-6 text-center">
                  <div className="text-sm text-muted-foreground">Your Donation</div>
                  <div className="font-serif text-4xl font-bold text-foreground mt-1">
                    ${getFinalAmount().toFixed(2)}
                  </div>
                </div>
              )}

              {/* Submit */}
              <Button
                onClick={handleDonate}
                disabled={isProcessing || getFinalAmount() < 1}
                className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-14 text-lg btn-hover"
                data-testid="donate-submit-button"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Heart className="w-5 h-5 mr-2" />
                    Donate {getFinalAmount() > 0 ? `$${getFinalAmount().toFixed(2)}` : "Now"}
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Secure payment processed by Stripe. Hope Hospitality is a registered 501(c)(3) nonprofit.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
